import { Link, useLocation } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { HelpDialog } from "@/components/HelpDialog";
import { Button } from "@/components/ui/button";
import { MessageSquare, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export function Header() {
  const { toast } = useToast();
  const location = useLocation();

  const handleSendFeedback = () => {
    toast({
      title: "Feedback",
      description: "Feedback feature coming soon! Thank you for your interest.",
    });
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="border-b border-translator-border bg-translator-panel/50 backdrop-blur-sm safe-area-top">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
              <div className="p-1.5 sm:p-2 bg-gradient-primary rounded-lg">
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                  TranslateLoo AI
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Powered by AI</p>
              </div>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/about")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                About
              </Link>
              <Link
                to="/contact"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/contact")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                Contact
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <HelpDialog />
            <ThemeToggle />
            <Button
              variant="outline"
              onClick={handleSendFeedback}
              className="hover:bg-gradient-primary hover:text-white transition-bounce touch-target"
              aria-label="Send feedback"
            >
              <MessageSquare className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Send Feedback</span>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="flex md:hidden items-center justify-center gap-4 mt-3 pt-3 border-t border-translator-border">
          <Link
            to="/"
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              isActive("/") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Home
          </Link>
          <Link
            to="/about"
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              isActive("/about") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            About
          </Link>
          <Link
            to="/contact"
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              isActive("/contact") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Contact
          </Link>
        </nav>
      </div>
    </header>
  );
}
